import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CoreBox } from '@youscience/core';
import { searchFilters } from '@common-types';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';
import { brightpathTheme } from '@youscience/theme';

import useUserInfoStore from '@store/UserInfoStore';
import useMajorSearchStore from '@store/MajorSearchStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { ROUTES } from '@constants/ROUTES';
import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { PLAN_FOR_COLLEGE_CARDS } from '@constants/baseHomePage';

import { CardSlider, CardType } from '@components/CardSlider';
import { Banner } from '@components/Banner';

import HeroSection from '@pages/Home/components/HeroSection';
import PlanForCollege from '@pages/Home/components/PlanForCollege';

import baseHomeImage from '@images/base-home-page.jpg';

import AreasOfStudySection from '../AreasOfStudySection';
import EducationOpportunities from '../EducationOpportunities';

import { sxStyles } from './BaseHomePage.styles';
import { useBaseHomePageData } from '@hooks/useBaseHomePageData';
import useHasTakenBrainGames from '@hooks/useHasTakenBrainGames';

const SchoolSelection = lazy(() => import('./components/SchoolSelection'));

export const BaseHomePage = () => {
  const navigate = useNavigate();

  const [isOpenSchoolSelection, setIsOpenSchoolSelection] = useState(false);
  const [isFavoriteSlider, setIsFavoriteSlider] = useState(false);

  const schoolFavorites = useUserPreferencesStore((state) => state.schoolFavorites);
  const { nearbySchools } = useUserInfoStore.getState();

  const hasTakenBrainGames = useHasTakenBrainGames();

  const setSearchFilters = useMajorSearchStore((state) => state.setSearchFilters);

  const { baseHomePageData, isLoading } = useBaseHomePageData();

  const handleSetFilter = (filter: string, value: boolean) => () => {
    setSearchFilters({ [filter]: value });
  };

  const handleOpenSchoolSelection = () => {
    setIsOpenSchoolSelection(true);
  };

  const handleCloseSchoolSelection = () => {
    setIsOpenSchoolSelection(false);
  };

  useEffect(() => {
    setIsFavoriteSlider(schoolFavorites.length > 0);
  }, [schoolFavorites]);

  const getSchoolSection = () => {
    const sortedFavorites = [...schoolFavorites].sort((a, b) => {
      if (b.modifiedDate > a.modifiedDate) {
        return 1;
      }

      if (b.modifiedDate < a.modifiedDate) {
        return -1;
      }

      return 0;
    });

    let schoolsToDisplay;

    if (isFavoriteSlider) {
      schoolsToDisplay = sortedFavorites.slice(0, 5).map((collegeFavorite) => collegeFavorite.school);
    } else {
      schoolsToDisplay = nearbySchools ?? [];
    }

    return schoolsToDisplay.length > 0 ? (
      <CoreBox sx={sxStyles.schoolsContainer}>
        <CardSlider
          dataTestId="schoolsSlider"
          cardType={CardType.SCHOOL}
          title={isFavoriteSlider ? 'Your favorite schools' : 'Schools near you'}
          cardList={schoolsToDisplay}
          navigateLink={isFavoriteSlider ? ROUTES.FAVORITE_SCHOOLS : undefined}
          buttonTitle={isFavoriteSlider ? 'Go to favorites' : undefined}
          isDataLoading={isLoading}
        />
      </CoreBox>
    ) : null;
  };

  return (
    <ThemeProvider theme={brightpathTheme}>
      <ScopedCssBaseline />

      <CoreBox sx={sxStyles.heroBg}>
        <HeroSection
          title={BREADCRUMBS_PAGE_TEXT.ROOT}
          subtitle="Whether you want to attend a 4-year university, community college, or land a job, explore programs and training opportunities to prepare for your new career right here."
          subtitle2="Have you graduated high school and enrolled in a college? Click on the Claim my school button."
          button1Name="Start exploring"
          onButton1Click={() => navigate(ROUTES.SCHOOLS)}
          button2Name="Claim my school"
          onButton2Click={handleOpenSchoolSelection}
          image={baseHomeImage}
        />
      </CoreBox>

      <EducationOpportunities />

      <PlanForCollege cards={PLAN_FOR_COLLEGE_CARDS} />

      <Banner onOpenSchoolSelection={handleOpenSchoolSelection} />

      {getSchoolSection()}

      <CardSlider
        dataTestId="exploreMoreBasedOnFit"
        cardType={CardType.MAJOR}
        title="Recommended majors based on your strong overall fit careers"
        cardList={hasTakenBrainGames ? (baseHomePageData?.careerFitMajors ?? []) : []}
        navigateLink={ROUTES.MAJORS}
        buttonTitle="Explore more"
        onButtonClick={handleSetFilter(searchFilters.personalRecommendation.fieldNames.isBestFitSoc, true)}
        isDataLoading={isLoading}
      />

      <CardSlider
        dataTestId="recommendedMajorsBasedOnFavoriteCareers"
        cardType={CardType.MAJOR}
        title="Recommended majors based on careers you have favorited"
        cardList={hasTakenBrainGames ? (baseHomePageData?.careerFavoriteMajors ?? []) : []}
        navigateLink={ROUTES.MAJORS}
        buttonTitle="Explore more"
        onButtonClick={handleSetFilter(searchFilters.personalRecommendation.fieldNames.isFavoriteSoc, true)}
        isDataLoading={isLoading}
      />

      <AreasOfStudySection />

      {isOpenSchoolSelection ? (
        <Suspense fallback={null}>
          <SchoolSelection
            isOpenSchoolSelection={isOpenSchoolSelection}
            onCloseSchoolSelection={handleCloseSchoolSelection}
          />
        </Suspense>
      ) : null}
    </ThemeProvider>
  );
};
